/**
 * The agent beside the user in PSI (Compliance Agent phase E).
 *
 * The strip: under a pending item, what the agent noticed about that record,
 * what it offers, and the answers: Yes (after reading the exact email for a
 * mail kind, with edits allowed and the recipients chosen among the
 * organization's registered users; the greeting follows their registered
 * names), Not now, Not useful, Covered outside the
 * system (the person's note is the record; evidence goes through the
 * existing Add a document). What was done is shown with the time and, where
 * there is one, Undo.
 *
 * The organization's brand color marks the agent's label and its yes, as on the notices.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { api } from './api.js';

const YES = { follow_up: 'Yes, send it', request_update: 'Yes, ask for a status', ask_questions: 'Yes, ask them', close_memo: 'Yes, close it' };
const ALT = { close_memo: 'Close it instead', follow_up: 'Send a follow up instead', request_update: 'Ask for a status instead', ask_questions: 'Ask the questions instead' };

export function useAgentProposals(enabled) {
  const [proposals, setProposals] = useState([]);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((k) => k + 1), []);
  useEffect(() => {
    if (!enabled) return undefined;
    let dead = false;
    api('/proposals').then((d) => { if (!dead && d?.success) setProposals(d.proposals || []); }).catch(() => {});
    return () => { dead = true; };
  }, [enabled, tick]);
  const byObject = new Map();
  for (const p of proposals) { const k = p.object_key; if (!byObject.has(k)) byObject.set(k, []); byObject.get(k).push(p); }
  return { proposals, byObject, refresh, live: proposals.filter((p) => p.status === 'proposed').length };
}

/** The PSI item's object key, matching the agent's: memos and issues only for now. */
export function objectKeyOfItem(item) {
  if (!item?.source_entity_id) return null;
  if (item.source_entity_type === 'memo') return `memo:${item.source_entity_id}`;
  if (item.source_entity_type === 'compliance_issue') return `issue:${item.source_entity_id}`;
  return null;
}

const when = (d) => (d ? new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');
const whenDay = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

let peoplePromise = null;
/** Registered users of the organization, loaded once per page: the only people an email can be addressed to. */
const loadPeople = () => { peoplePromise = peoplePromise || api('/people').then((d) => (d?.success ? d.people || [] : [])).catch(() => []); return peoplePromise; };
const GREETING = /^(Dear|Hi|Hello)\b[^\n]*,/;
const firstNameOf = (n) => String(n || '').trim().split(/\s+/)[0] || '';
const joinNames = (xs) => (xs.length <= 1 ? xs.join('') : xs.length === 2 ? `${xs[0]} and ${xs[1]}` : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
/** The greeting as the server writes it from registered names, shown here so what you see is what goes. */
export const greetingFor = (list) => { const names = [...new Set(list.map((r) => firstNameOf(r.name)).filter((n) => n && !n.includes('@')))]; return names.length ? `Dear ${joinNames(names)},` : 'Dear colleagues,'; };
const sameEmail = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();
const asRecipient = (r) => ({ ...(r.id ? { user_id: r.id } : {}), email: r.email });

function Btn({ children, onClick, primary, color, disabled, t, title }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      className={`text-[12px] font-semibold rounded-[7px] px-[11px] py-[5px] border transition-colors disabled:opacity-50 ${primary ? 'text-white border-transparent' : t('bg-white border-slate-200 text-slate-800 hover:border-slate-400', 'bg-white/[0.04] border-white/10 text-slate-200 hover:bg-white/[0.08]')}`}
      style={primary ? { backgroundColor: color } : undefined}>{children}</button>
  );
}

export function ProposalStrip({ p, t, brand, onChanged, onAddDocument, inline = true, initialMode = null }) {
  const color = brand?.color || '#E5601F';
  const [mode, setMode] = useState(initialMode); // null | 'read' | 'covered' | 'busy'
  const [subject, setSubject] = useState(p.draft?.subject || '');
  const [body, setBody] = useState(p.draft?.body_text || '');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [state, setState] = useState(p);
  const [people, setPeople] = useState([]);
  const [recips, setRecips] = useState(p.recipients || []);
  const [recipsChanged, setRecipsChanged] = useState(false);
  useEffect(() => { setState(p); }, [p]);

  const call = async (path, payload) => {
    setErr(null); setMode('busy');
    const d = await api(path, { method: 'POST', body: payload || {} });
    if (!d?.success) { setErr(d?.message || 'That did not go through.'); setMode(null); return null; }
    setMode(null);
    return d;
  };
  const approve = async (alternative = null) => {
    const d = await call(`/proposals/${p.id}/approve`, state.mail && !alternative ? { subject, body_text: body, ...(recipsChanged ? { recipients: recips.map(asRecipient) } : {}) } : { note: note || undefined, alternative: alternative || undefined });
    if (d?.proposal) { setState(d.proposal); onChanged?.(); }
  };
  const decline = async (reason) => {
    const d = await call(`/proposals/${p.id}/decline`, { reason, note: note || undefined });
    if (d) { setState({ ...state, status: d.status, hold_until: d.hold_until, decision_note: note || null, kind_stopped: d.kind_stopped }); onChanged?.(); }
  };
  const reverse = async () => {
    const d = await call(`/proposals/${p.id}/reverse`, {});
    if (d) { setState({ ...state, status: 'reversed' }); onChanged?.(); }
  };
  const read = async () => {
    if (mode === 'read') { setMode(null); return; }
    setMode('read');
    if (state.mail) loadPeople().then(setPeople);
    if (!preview) { const d = await api(`/proposals/${p.id}/preview`, { method: 'POST', body: {} }); if (d?.success) setPreview(d); }
  };
  /** Who receives it. At least one person; the greeting line follows the names; the server checks every name against the organization's users. */
  const setRecipients = async (next) => {
    if (!next.length) return;
    setRecips(next); setRecipsChanged(true); setErr(null);
    setBody((b) => (GREETING.test(b) ? b.replace(GREETING, greetingFor(next)) : b));
    const d = await api(`/proposals/${p.id}/preview`, { method: 'POST', body: { recipients: next.map(asRecipient) } });
    if (d?.success) setPreview(d); else if (d?.message) setErr(d.message);
  };

  const box = `${inline ? 'border-t' : 'rounded-[12px] border'} ${t('border-orange-100', 'border-white/[0.08]')}`;
  const bg = t('bg-[#FFF7F3]', 'bg-white/[0.03]');
  const text = t('text-slate-800', 'text-slate-200');
  const dim = t('text-slate-500', 'text-slate-400');

  const outcome = () => {
    const r = state.result || {};
    if (state.status === 'done') return <>Done {when(state.decided_at)}{r.recipients?.length ? ` · sent to ${r.recipients.join(', ')}` : r.closed ? ` · memo ${r.memo_number || ''} closed` : ''} · recorded under your name{state.reversible ? <> · <button type="button" onClick={reverse} className="underline" style={{ color }}>Undo</button></> : ' · mail that has gone stays sent'}{state.reply && <span className="block mt-0.5" data-testid="agent-reply">{state.reply.from} replied by email {whenDay(state.reply.at)}{state.reply.rca ? ' with the root cause form, recorded on the issue' : ', recorded on the record'}{state.reply.excerpt ? <>: <span className={dim}>“{state.reply.excerpt}”</span></> : '.'}</span>}</>;
    if (state.status === 'failed') return <>Could not do it: {r.error || 'unknown error'}. Nothing was changed.</>;
    if (state.status === 'reversed') return <>Reversed {when(new Date())} · the memo is open again, with its own comment and audit entry.</>;
    if (state.status === 'held') return <>Held. I will raise it again after {state.hold_until ? new Date(state.hold_until).toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : 'a week'} if nothing changes.</>;
    if (state.status === 'declined') return <>Noted as not useful. I will not raise this on this record again{state.kind_stopped ? ', and I have stopped this kind of proposal for you' : ''}.</>;
    if (state.status === 'covered') return <>Recorded as covered outside the system{state.decision_note ? `: “${state.decision_note}”` : ''}. Attach the evidence through Add a document on the record.</>;
    if (state.status === 'expired') return <>The record changed; this no longer applies.</>;
    return null;
  };

  return (
    <div className={`${box} ${bg} px-[14px] py-[10px] text-[13px] leading-[1.45]`} data-testid="agent-proposal" data-kind={state.kind}>
      <div className="min-w-0">
        {!inline && <div className={`text-[12.5px] font-semibold mb-0.5 ${text}`}>{state.object_label}{state.href && <a href={state.href} className="ml-2 text-[12px] font-semibold underline" style={{ color }}>Open the record</a>}</div>}
        <div className={text}>{state.noticed} <span className="font-semibold">{state.offer}</span></div>
        {state.reasoning && <div className={`text-[12px] mt-0.5 ${dim}`}>{state.reasoning}</div>}
        {state.status === 'proposed' && mode !== 'covered' && (
          <>
            {mode === 'read' && state.mail && (
              <div className={`mt-2 rounded-[8px] border p-[10px] ${t('bg-white border-orange-100', 'bg-black/20 border-white/[0.08]')}`}>
                <div className="flex flex-wrap items-center gap-[6px] mb-1" data-testid="agent-recipients">
                  <span className={`text-[11px] uppercase tracking-[0.06em] font-bold ${dim}`}>To</span>
                  {recips.map((r) => (
                    <span key={r.email} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] ${t('bg-slate-100 text-slate-700', 'bg-white/10 text-slate-200')}`}>
                      {r.name || r.email}
                      {recips.length > 1 && <button type="button" aria-label={`Remove ${r.name || r.email}`} title="Remove" onClick={() => setRecipients(recips.filter((x) => !sameEmail(x.email, r.email)))} className="opacity-60 hover:opacity-100">✕</button>}
                    </span>
                  ))}
                  <select value="" aria-label="Add a recipient" onChange={(e) => { const u = people.find((x) => String(x.id) === e.target.value); if (u && !recips.some((r) => sameEmail(r.email, u.email))) setRecipients([...recips, u]); }}
                    className={`text-[12px] rounded-[7px] border px-[7px] py-[3px] ${t('bg-white border-slate-200 text-slate-700', 'bg-black/30 border-white/10 text-slate-200')}`}>
                    <option value="">Add someone…</option>
                    {people.filter((u) => !recips.some((r) => sameEmail(r.email, u.email))).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                  </select>
                  <span className={`text-[11px] ${dim}`}>through Workstream, under your name · registered users of your organization only</span>
                </div>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Subject" className={`w-full text-[13px] font-semibold rounded-[6px] border px-2 py-1 mb-1 ${t('bg-white border-slate-200 text-slate-900', 'bg-black/30 border-white/10 text-slate-100')}`} />
                <textarea value={body} onChange={(e) => setBody(e.target.value)} aria-label="Body" rows={Math.min(14, Math.max(5, body.split('\n').length + 1))} className={`w-full text-[13px] leading-[1.5] rounded-[6px] border px-2 py-1 ${t('bg-white border-slate-200 text-slate-900', 'bg-black/30 border-white/10 text-slate-100')}`} />
                <div className={`text-[11.5px] mt-1 ${dim}`}>Workstream adds the organization header, the record, the acknowledge link and the close with your name{preview?.attachments?.length ? `, and attaches ${preview.attachments.join(', ')}` : ''}. Edit anything above; what you see is what goes, and your edits teach the agent how the Department writes. The greeting follows the recipients' registered names.</div>
              </div>
            )}
            {mode === 'read' && !state.mail && state.kind === 'close_memo' && (
              <div className={`mt-2 rounded-[8px] border p-[10px] ${t('bg-white border-orange-100', 'bg-black/20 border-white/[0.08]')}`}>
                <div className={`text-[11px] uppercase tracking-[0.06em] font-bold mb-1 ${dim}`}>Close note, the record of why it is complete</div>
                <input value={note || state.note || ''} onChange={(e) => setNote(e.target.value)} aria-label="Close note" className={`w-full text-[13px] rounded-[6px] border px-2 py-1 ${t('bg-white border-slate-200 text-slate-900', 'bg-black/30 border-white/10 text-slate-100')}`} />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-[6px] mt-[7px]">
              {mode !== 'read' && (state.mail || state.kind === 'close_memo') && <Btn onClick={read} t={t}>{state.mail ? 'Show me what you would send' : 'Show me the close note'}</Btn>}
              <Btn primary color={color} onClick={() => approve()} disabled={mode === 'busy' || (state.mail && recips.length === 0)} t={t}>{mode === 'read' ? (state.mail ? 'Send it' : 'Close it') : YES[state.kind] || 'Yes'}</Btn>
              {state.alternatives.map((a) => <Btn key={a.kind} onClick={() => approve(a.kind)} disabled={mode === 'busy'} t={t}>{ALT[a.kind] || a.label}</Btn>)}
              <Btn onClick={() => decline('not_now')} disabled={mode === 'busy'} t={t} title="Held for a week, then raised again if nothing changes">Not now</Btn>
              <Btn onClick={() => decline('not_useful')} disabled={mode === 'busy'} t={t} title="Never again on this record while it is unchanged">Not useful</Btn>
              <Btn onClick={() => setMode('covered')} disabled={mode === 'busy'} t={t}>Covered outside the system</Btn>
            </div>
          </>
        )}
        {state.status === 'proposed' && mode === 'covered' && (
          <div className="flex flex-wrap items-center gap-[6px] mt-[7px]">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="How it was covered, for example by email to CMS on October 20" aria-label="How it was covered" className={`flex-1 min-w-[220px] text-[12.5px] rounded-[7px] border px-[9px] py-[6px] ${t('bg-white border-slate-200 text-slate-900', 'bg-black/30 border-white/10 text-slate-100')}`} />
            <Btn primary color={color} onClick={() => decline('covered')} disabled={!note.trim()} t={t}>Record it</Btn>
            {onAddDocument && <Btn onClick={onAddDocument} t={t}>Attach evidence with Add a document</Btn>}
            <Btn onClick={() => setMode(null)} t={t}>Back</Btn>
          </div>
        )}
        {state.status !== 'proposed' && <div className={`mt-[6px] text-[12px] font-mono ${dim}`}>{outcome()}</div>}
        {err && <div className="mt-1 text-[12px] text-red-600">{err}</div>}
      </div>
    </div>
  );
}

/** Proposals whose record is not in the visible list: shown as their own band. */
export function AlsoNoticed({ proposals, t, brand, onChanged, onAddDocument }) {
  if (!proposals.length) return null;
  return (
    <div className="mb-[22px]" data-testid="agent-also-noticed">
      <div className="flex items-baseline gap-2.5 mb-2">
        <h2 className={`text-[13px] font-bold tracking-[0.01em] ${t('text-slate-800', 'text-slate-200')}`}>Also noticed</h2>
        <span className={`text-[12px] ${t('text-slate-500', 'text-slate-500')}`}>{proposals.length === 1 ? 'one record not in the list above' : `${proposals.length} records not in the list above`}</span>
      </div>
      <div className="flex flex-col gap-2">
        {proposals.map((p) => <ProposalStrip key={p.id} p={p} t={t} brand={brand} onChanged={onChanged} onAddDocument={onAddDocument} inline={false} />)}
      </div>
    </div>
  );
}
